import {
  Stack,
  TreatmentCard,
  type TreatmentCardProps,
} from "@noom/wax-component-library";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

import { useTrackCareTabViewed } from "./hooks/useTrackCareTabViewed";
import { useTrackMedCardDisplayed } from "./hooks/useTrackMedCardDisplayed";
import type { TreatmentOverview } from "./hooks/useTreatmentOverviews";
import { getLabTreatmentCardProps } from "./labTreatmentCardMapper";
import {
  type GetTreatmentCardPropsHandlers,
  type TreatmentCardMapperOptions,
  getTreatmentCardProps,
} from "./treatmentCardMapper";
import { isLabTreatmentOverview } from "./treatmentOverviewGuards";

type CareTabTreatmentListProps = {
  treatments: TreatmentOverview[];
  handlers: GetTreatmentCardPropsHandlers;
  isSupplementTreatmentDetailEnabled?: boolean;
  isBiomarkerTreatmentDetailEnabled?: boolean;
};

export const getCardPropsFor = (
  treatment: TreatmentOverview,
  t: TFunction<"care">,
  handlers: GetTreatmentCardPropsHandlers,
  options?: TreatmentCardMapperOptions
): TreatmentCardProps =>
  isLabTreatmentOverview(treatment)
    ? getLabTreatmentCardProps(treatment, t, handlers, options)
    : getTreatmentCardProps(treatment, t, handlers, options);

type CareTabTreatmentCardProps = {
  treatment: TreatmentOverview;
  handlers: GetTreatmentCardPropsHandlers;
  isSupplementTreatmentDetailEnabled: boolean;
  isBiomarkerTreatmentDetailEnabled: boolean;
};

/**
 * Per-card leaf that owns the `medCardDisplayed` impression. Keeping the
 * hook here (not in the list) means each card fires exactly once for its
 * own mount lifetime, independent of sibling remounts / list rerenders.
 */
const CareTabTreatmentCard = ({
  treatment,
  handlers,
  isSupplementTreatmentDetailEnabled,
  isBiomarkerTreatmentDetailEnabled,
}: CareTabTreatmentCardProps) => {
  const { t } = useTranslation("care", { useSuspense: false });
  useTrackMedCardDisplayed(treatment);

  return (
    <div data-cy={`treatment-card-${treatment.treatmentCode}`}>
      <TreatmentCard
        width="100%"
        {...getCardPropsFor(treatment, t, handlers, {
          isSupplementTreatmentDetailEnabled,
          isBiomarkerTreatmentDetailEnabled,
        })}
      />
    </div>
  );
};

/**
 * Leaf component that renders the treatment cards AND fires
 * `care_tab_viewed`. Mounting this only when treatments are ready means
 * the analytics fires alongside the content the user actually sees, and
 * `useTrackCareTabViewed` stays agnostic of upstream loading / error
 * state.
 */
export const CareTabTreatmentList = ({
  treatments,
  handlers,
  isSupplementTreatmentDetailEnabled = false,
  isBiomarkerTreatmentDetailEnabled = false,
}: CareTabTreatmentListProps) => {
  useTrackCareTabViewed();

  // Render the cards inside their own spacing Stack: the parent page mounts
  // this list as a single child, so cards returned in a bare fragment all
  // collapse into one `chakra-stack__item` and the page Stack's `spacing`
  // never separates them (0px gap between adjacent cards).
  return (
    <Stack spacing={4} width="100%">
      {treatments.map((treatment, index) => (
        <CareTabTreatmentCard
          key={`${treatment.__typename}-${treatment.treatmentCode}-${index}`}
          treatment={treatment}
          handlers={handlers}
          isSupplementTreatmentDetailEnabled={
            isSupplementTreatmentDetailEnabled
          }
          isBiomarkerTreatmentDetailEnabled={isBiomarkerTreatmentDetailEnabled}
        />
      ))}
    </Stack>
  );
};
